// frontend/src/context/AuthContext.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Created:      2025-06-18
// Updated:      2026-06-22
//
// Description:
//   Auth context for managing user authentication state, heartbeat ping, secure login/logout,
//   and tenant-change invalidation support.
//
// AUD8-FE-STATE:
//   - Sets/clears global axios Authorization header consistently.
//   - Exposes authScopeKey so the app can remount tenant-scoped UI after tenant/user/token changes.
//   - Prevents stale tenant data hanging around after switching between tenants.

import React, { createContext, useState, useEffect, useContext, useRef, useMemo } from "react";
import axios from "axios";

const defaultAuth = {
  user: null,
  token: null,
  loading: false,
  authScopeKey: "anon",
  login: async () => false,
  logout: async () => {},
  refreshCurrentUser: async () => null,
};

const AuthContext = createContext(defaultAuth);

export const AuthProvider = ({ children }) => {
  const heartbeatRef = useRef(null);

  const [user, setUser] = useState(defaultAuth.user);
  const [token, setToken] = useState(defaultAuth.token);
  const [loading, setLoading] = useState(true);

  const normalizeToken = (val) => (val ? val.replace(/^Bearer\s+/i, "") : null);

  const applyAxiosAuth = (jwt) => {
    if (jwt) {
      axios.defaults.headers.common.Authorization = `Bearer ${jwt}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  };

  const clearHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  const startHeartbeat = (jwt) => {
    clearHeartbeat();

    heartbeatRef.current = setInterval(() => {
      axios
        .post("/api/users/ping", null, {
          headers: { Authorization: `Bearer ${jwt}` },
        })
        .catch((err) => {
          console.warn("Heartbeat ping failed:", err?.response?.status || err.message);
        });
    }, 60000);
  };

  const clearAuthState = () => {
    clearHeartbeat();
    localStorage.removeItem("anpr_token");
    sessionStorage.removeItem("anpr_token");
    applyAxiosAuth(null);
    setToken(null);
    setUser(null);
  };

  const fetchCurrentUser = async (jwt) => {
    const cleanJwt = normalizeToken(jwt);

    if (!cleanJwt) {
      clearAuthState();
      setLoading(false);
      return null;
    }

    try {
      applyAxiosAuth(cleanJwt);

      const res = await axios.get("/api/users/me", {
        headers: { Authorization: `Bearer ${cleanJwt}` },
      });

      setUser(res.data);
      setToken(cleanJwt);
      startHeartbeat(cleanJwt);
      return res.data;
    } catch (err) {
      console.warn("Failed to fetch current user:", err?.response?.status || err.message);
      clearAuthState();
      return null;
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrentUser = async () => {
    const jwt =
      token ||
      normalizeToken(localStorage.getItem("anpr_token")) ||
      normalizeToken(sessionStorage.getItem("anpr_token"));

    return fetchCurrentUser(jwt);
  };

  useEffect(() => {
    const stored =
      localStorage.getItem("anpr_token") || sessionStorage.getItem("anpr_token");
    const jwt = normalizeToken(stored);

    if (jwt) {
      setToken(jwt);
      applyAxiosAuth(jwt);
      fetchCurrentUser(jwt);
    } else {
      applyAxiosAuth(null);
      setLoading(false);
    }

    return () => {
      clearHeartbeat();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, password) => {
    try {
      const res = await axios.post("/api/login", { username, password });
      const jwt =
        normalizeToken(res?.data?.access_token) ||
        normalizeToken(res?.data?.token);

      if (!jwt) throw new Error("No access token returned from /api/login");

      localStorage.setItem("anpr_token", jwt);
      applyAxiosAuth(jwt);
      setToken(jwt);

      const loadedUser = await fetchCurrentUser(jwt);
      return !!loadedUser;
    } catch (e1) {
      try {
        const form = new URLSearchParams();
        form.append("username", username);
        form.append("password", password);

        const r2 = await axios.post("/api/auth/token", form, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });

        const jwt =
          normalizeToken(r2?.data?.access_token) ||
          normalizeToken(r2?.data?.token);

        if (!jwt) throw new Error("No access token returned from /api/auth/token");

        localStorage.setItem("anpr_token", jwt);
        applyAxiosAuth(jwt);
        setToken(jwt);

        const loadedUser = await fetchCurrentUser(jwt);
        return !!loadedUser;
      } catch (e2) {
        console.error("Login failed:", e2?.response?.status, e2?.response?.data || e2.message);
        clearAuthState();
        return false;
      }
    }
  };

  const logout = async () => {
    const jwt = token || normalizeToken(localStorage.getItem("anpr_token"));

    if (jwt) {
      try {
        await axios.post(
          "/api/logout",
          null,
          { headers: { Authorization: `Bearer ${jwt}` } }
        );
      } catch (e) {
        console.warn("Logout log failed:", e?.response?.status || e.message);
      }
    }

    clearAuthState();
    window.location.replace("/login");
  };

  const authScopeKey = useMemo(() => {
    const tenantId = user?.tenant_id || "anon";
    const userId = user?.id || "anon";
    const tokenTail = token ? token.slice(-10) : "no-token";
    return `tenant-${tenantId}-user-${userId}-${tokenTail}`;
  }, [user?.tenant_id, user?.id, token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        loading,
        authScopeKey,
        refreshCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext) || defaultAuth;
};